import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CountryPicker from "../../../main/component/CountryPicker";

// Helper function to extract string value from Immutable or plain object
const extractStringValue = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;

  const plain = typeof value?.toJS === "function" ? value.toJS() : value;
  if (plain && typeof plain === "object") {
    return plain.label ?? plain.value ?? "";
  }

  return String(plain || "");
};

interface AddressGroupFieldProps {
  street: string;
  city: string;
  state: string;
  country?: string;
  onStreetChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onCountryChange?: (value: string) => void;
  autoFocus?: boolean;
}

const AddressGroupField: React.FC<AddressGroupFieldProps> = ({
  street,
  city,
  state,
  country,
  onStreetChange,
  onCityChange,
  onStateChange,
  onCountryChange,
  autoFocus = false,
}) => {
  const { t } = useTranslation();

  // State management
  const [streetValue, setStreetValue] = useState(() => extractStringValue(street));
  const [cityValue, setCityValue] = useState(() => extractStringValue(city));
  const [stateValue, setStateValue] = useState(() => extractStringValue(state));
  const [countryValue, setCountryValue] = useState(country);

  // Refs
  const streetInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const stateInputRef = useRef<HTMLInputElement>(null);

  // Sync values when props change
  useEffect(() => {
    setStreetValue(extractStringValue(street));
    setCityValue(extractStringValue(city));
    setStateValue(extractStringValue(state));
    setCountryValue(country);
  }, [street, city, state, country]);

  // Auto focus on street input when opened
  useEffect(() => {
    if (autoFocus && streetInputRef.current) {
      setTimeout(() => {
        streetInputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  // Generic handlers for text inputs
  const createBlurHandler = (
    currentValue: string,
    propValue: any,
    onChange: (value: string) => void
  ) => {
    return () => {
      const extractedValue = extractStringValue(propValue);
      if (currentValue !== extractedValue) {
        onChange(currentValue);
      }
    };
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  // Field-specific handlers
  const handleStreetBlur = createBlurHandler(streetValue, street, onStreetChange);
  const handleCityBlur = createBlurHandler(cityValue, city, onCityChange);
  const handleStateBlur = createBlurHandler(stateValue, state, onStateChange);

  // Shared input styles
  const inputClassName =
    "w-full rounded-md border border-gray-300 px-2 py-1.5 text-[12px] leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const labelClassName = "text-[12px] leading-4 font-medium text-gray-700";
  const fieldContainerClassName = "space-y-1 flex flex-col";

  // Render text input field
  const renderTextInput = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    onBlur: () => void,
    placeholder: string,
    inputRef: React.RefObject<HTMLInputElement>
  ) => (
    <div className={fieldContainerClassName}>
      <label className={labelClassName}>{label}</label>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleEnterKey}
        placeholder={placeholder}
        className={inputClassName}
      />
    </div>
  );

  return (
    <div className="w-full space-y-3">
      {renderTextInput(
        t("Address"),
        streetValue,
        setStreetValue,
        handleStreetBlur,
        t("Street address"),
        streetInputRef
      )}

      {renderTextInput(t("City"), cityValue, setCityValue, handleCityBlur, t("City"), cityInputRef)}

      {renderTextInput(
        t("State"),
        stateValue,
        setStateValue,
        handleStateBlur,
        t("State"),
        stateInputRef
      )}

      {/* Country Field */}
      {onCountryChange && (
        <div className="space-y-1">
          <label className="text-[12px] leading-4 font-medium text-gray-700">{t("Country")}</label>
          <div className="w-full">
            <CountryPicker
              item={countryValue}
              onChange={(countryVal) => {
                setCountryValue(countryVal);
                onCountryChange(countryVal);
              }}
              variant="inline"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressGroupField;
